import type { Auth, User } from "firebase/auth";
import { type FormEvent, useEffect, useState } from "react";
import * as api from "./api";
import { actionText, actorText, chainLine, downloadFailure, eventChangesText, EXPORTS, recordText, saveBlob, sourceNote } from "./exports";
import { readingTime } from "./format";
import { go, useTitle } from "./hooks";
import type { Saved } from "./save";
import { type Failure, failureOf, Problem, Status } from "./UserPages";
import { Skeleton } from "./Skeleton";

/** Design 26.11 F6: the CSV files of the organization, for the administrator and the operations manager. */
export function ExportsPage({ org, auth, user, canView }: { org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [busy, setBusy] = useState<api.ExportKind | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  useTitle(`Exports · ${org.name}`);
  if (canView === false) {
    return (
      <main data-testid="exports-hidden">
        <h1 tabIndex={-1}>Exports</h1>
        <p>Only the administrator and the operations manager download the files of the organization.</p>
      </main>
    );
  }
  const download = async (kind: api.ExportKind) => {
    setBusy(kind);
    setSaved(null);
    const started = performance.now();
    try {
      const file = await api.exportFile(await user.getIdToken(), kind);
      saveBlob(file.name, file.blob);
      console.info(`exports: ${kind} ${file.blob.size} bytes in ${Math.round(performance.now() - started)} ms`);
      setSaved({ ok: true, text: `Downloaded ${file.name}.` });
    } catch (e) {
      const status = e instanceof api.ApiError ? e.status : 0;
      console.warn(`exports: ${kind} failed with ${status} after ${Math.round(performance.now() - started)} ms`);
      setSaved(downloadFailure(status));
    } finally {
      setBusy(null);
    }
  };
  return (
    <main className="exports-page wide" data-testid="exports-page">
      <h1 tabIndex={-1}>Exports</h1>
      <p className="sub">CSV files of {org.name}, for a check outside the app and for the way back to the sheet. The activity of the organization shows each download.</p>
      <ul className="export-list" data-testid="export-list">
        {EXPORTS.map((file) => (
          <li key={file.kind} data-testid="export-row" data-kind={file.kind}>
            <div>
              <strong>{file.label}</strong>
              <p className="sub">{file.text}</p>
            </div>
            <button type="button" data-testid={`export-${file.kind}`} disabled={busy !== null} onClick={() => void download(file.kind)}>
              {busy === file.kind ? "Downloading…" : "Download CSV"}
            </button>
          </li>
        ))}
      </ul>
      <Status saved={saved} auth={auth} testId="export-status" />
    </main>
  );
}

type Shown = { list: api.ActivityList; events: api.ActivityEvent[]; next: number | null };
type Filters = { action: string; actor: string; account: string };
const NO_FILTERS: Filters = { action: "", actor: "", account: "" };

/** Design 26.11 F6: the audit of the organization, filtered by action, user and account, and the chain check. */
export function ActivityPage({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [account, setAccount] = useState("");
  const [found, setFound] = useState<Shown | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [more, setMore] = useState<"idle" | "loading" | "failed">("idle");
  const [known, setKnown] = useState<Pick<api.ActivityList, "actions" | "members">>({ actions: [], members: [] });
  useTitle(`Activity · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.activityList(token, filters))
      .then((list) => {
        if (!live) return;
        setFound({ list, events: list.events, next: list.next_before });
        setKnown({ actions: list.actions, members: list.members });
      })
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, filters, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="activity-hidden">
        <h1 tabIndex={-1}>Activity</h1>
        <p>Only an administrator of the whole organization sees the activity.</p>
      </main>
    );
  }
  const shown = typeof found === "object" ? found : null;
  const when = (iso: string) => readingTime(iso, org.time_zone);
  const chain = shown ? chainLine(shown.list.chain_check, when) : null;
  const older = async () => {
    if (!shown || shown.next === null) return;
    setMore("loading");
    try {
      const list = await api.activityList(await user.getIdToken(), { ...filters, before: shown.next });
      setFound({ ...shown, events: [...shown.events, ...list.events], next: list.next_before });
      setMore("idle");
    } catch {
      setMore("failed");
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters({ ...filters, account: account.trim() });
  };
  return (
    <main className="activity-page wide" data-testid="activity-page">
      <h1 tabIndex={-1}>Activity</h1>
      <p className="sub">Each change and each download in {org.name}, the newest first. The nightly check computes the hash chain of the events again.</p>
      {shown && (
        <p data-testid="activity-chain" data-ok={chain ? String(chain.ok) : "none"} role={chain && !chain.ok ? "alert" : undefined} className={chain && !chain.ok ? "error" : undefined}>
          {chain ? chain.text : "No chain check yet. The nightly job checks the chain."}
        </p>
      )}
      <form className="filters" data-testid="activity-filters" onSubmit={submit}>
        <label>
          Action{" "}
          <select data-testid="activity-action" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}>
            <option value="">All actions</option>
            {known.actions
              .map((action) => ({ action, text: actionText(action) }))
              .sort((a, b) => a.text.localeCompare(b.text))
              .map(({ action, text }) => (
                <option key={action} value={action}>
                  {text}
                </option>
              ))}
          </select>
        </label>
        <label>
          User{" "}
          <select data-testid="activity-actor" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })}>
            <option value="">All users</option>
            {known.members.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name ? `${person.name} (${person.email})` : person.email}
              </option>
            ))}
          </select>
        </label>
        <label>
          Account number{" "}
          <input data-testid="activity-account" value={account} maxLength={40} onChange={(e) => setAccount(e.target.value)} />
        </label>
        <button type="submit" data-testid="activity-apply">
          Show
        </button>
        {(filters.action || filters.actor || filters.account) && (
          <button
            type="button"
            data-testid="activity-clear"
            onClick={() => {
              setAccount("");
              setFilters(NO_FILTERS);
            }}
          >
            Clear filters
          </button>
        )}
      </form>
      {found === undefined ? (
        <Skeleton shape="table" columns={6} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : found.list.unknown_account ? (
        <p data-testid="activity-unknown-account">No account has the number {filters.account}.</p>
      ) : !found.events.length ? (
        <p data-testid="activity-empty">No events match the filters.</p>
      ) : (
        <>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Activity">
            <table className="list" data-testid="activity-rows">
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">User</th>
                  <th scope="col">Action</th>
                  <th scope="col">Record</th>
                  <th scope="col">Change</th>
                </tr>
              </thead>
              <tbody>
                {found.events.map((event) => (
                  <tr key={event.seq} data-testid="activity-row" data-action={event.action}>
                    <td data-label="Time">{when(event.at)}</td>
                    <td data-label="User" className="wrap user">
                      {actorText(event)}
                    </td>
                    <td data-label="Action" className="wrap action">
                      {actionText(event.action)}
                      {sourceNote(event) && <span className="sub"> · {sourceNote(event)}</span>}
                    </td>
                    <td data-label="Record" className="wrap">
                      {event.resource_type === "account" && event.resource_label ? (
                        <a href={`${base}/accounts/${event.resource_id}`} onClick={go}>
                          {recordText(event)}
                        </a>
                      ) : (
                        recordText(event)
                      )}
                    </td>
                    <td data-label="Change" className="wrap change">
                      {eventChangesText(event.action, event.changes, when) || "–"}
                      {event.reason && <span className="sub"> · Why: {event.reason}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {found.next !== null && (
            <p>
              <button type="button" data-testid="activity-older" disabled={more === "loading"} onClick={() => void older()}>
                {more === "loading" ? "Loading…" : "Show older events"}
              </button>
              {more === "failed" && <span className="error"> The older events did not load. Try again.</span>}
            </p>
          )}
        </>
      )}
    </main>
  );
}
